import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Banknote, Building2, CalendarDays, Clock, FileText, Inbox, LayoutDashboard, Layers, Settings, Sun, Users, Hand, CalendarCheck } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { Protected, isAdmin, isMember, isSuper } from "@/components/Protected";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Tour from "@/pages/Tour";
import Join from "@/pages/Join";
import { PaymentCancel, PaymentSuccess } from "@/pages/Payment";
import SuperOverview from "@/pages/super/SuperOverview";
import SuperBusinesses from "@/pages/super/SuperBusinesses";
import SuperPlans from "@/pages/super/SuperPlans";
import SuperContent from "@/pages/super/SuperContent";
import Rota from "@/pages/admin/Rota";
import Staff from "@/pages/admin/Staff";
import Requests from "@/pages/admin/Requests";
import Timesheets from "@/pages/admin/Timesheets";
import SettingsPage from "@/pages/admin/Settings";
import Billing from "@/pages/admin/Billing";
import StaffHome from "@/pages/staff/StaffHome";
import MyMonth from "@/pages/staff/MyMonth";
import OpenShifts from "@/pages/staff/OpenShifts";
import MyRequests from "@/pages/staff/MyRequests";
import Availability from "@/pages/staff/Availability";

const superNav = [
  { id: "overview", to: "/super", end: true, label: "Overview", icon: LayoutDashboard },
  { id: "businesses", to: "/super/businesses", label: "Businesses", icon: Building2 },
  { id: "plans", to: "/super/plans", label: "Plans", icon: Layers },
  { id: "content", to: "/super/content", label: "Site content", icon: FileText },
];
const adminNav = [
  { id: "rota", to: "/app", end: true, label: "Rota", icon: CalendarDays },
  { id: "staff", to: "/app/staff", label: "Staff", icon: Users },
  { id: "requests", to: "/app/requests", label: "Requests", icon: Inbox },
  { id: "timesheets", to: "/app/timesheets", label: "Timesheets", icon: Clock },
  { id: "settings", to: "/app/settings", label: "Settings", icon: Settings },
  { id: "billing", to: "/app/billing", label: "Billing", icon: Banknote },
  { id: "my-shifts", to: "/me", label: "My shifts", icon: Sun },
];
const staffNav = [
  { id: "home", to: "/me", end: true, label: "Home", icon: Sun },
  { id: "month", to: "/me/month", label: "My month", icon: CalendarDays },
  { id: "open", to: "/me/open", label: "Open shifts", icon: Hand },
  { id: "my-requests", to: "/me/requests", label: "Requests", icon: Inbox },
  { id: "availability", to: "/me/availability", label: "Availability", icon: CalendarCheck },
];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ImpersonationBanner />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/tour" element={<Tour />} />
          <Route path="/join/:slug" element={<Join />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/super" element={<Protected allow={isSuper}><AppShell nav={superNav} title="Super admin" /></Protected>}>
            <Route index element={<SuperOverview />} />
            <Route path="businesses" element={<SuperBusinesses />} />
            <Route path="plans" element={<SuperPlans />} />
            <Route path="content" element={<SuperContent />} />
          </Route>
          <Route path="/app" element={<Protected allow={isAdmin}><AppShell nav={adminNav} title="Business admin" /></Protected>}>
            <Route index element={<Rota />} />
            <Route path="staff" element={<Staff />} />
            <Route path="requests" element={<Requests />} />
            <Route path="timesheets" element={<Timesheets />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="billing" element={<Billing />} />
          </Route>
          <Route path="/me" element={<Protected allow={isMember}><StaffShell /></Protected>}>
            <Route index element={<StaffHome />} />
            <Route path="month" element={<MyMonth />} />
            <Route path="open" element={<OpenShifts />} />
            <Route path="requests" element={<MyRequests />} />
            <Route path="availability" element={<Availability />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster richColors position="bottom-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

function StaffShell() {
  return <AppShell nav={staffNav} title="My work" branded />;
}

export default App;
